import React, { useState, useEffect } from 'react';
import { EngineProfileId } from '../types';
import { api, ApiSound } from '../services/api';
import { Lock, Play, Sparkles, X } from 'lucide-react';

interface SoundCatalogueSheetProps {
  isOpen: boolean;
  onClose: () => void;
  currentSoundId: EngineProfileId;
  onSelectSound: (soundId: EngineProfileId) => void;
}

export const SoundCatalogueSheet: React.FC<SoundCatalogueSheetProps> = ({
  isOpen,
  onClose,
  currentSoundId,
  onSelectSound,
}) => {
  const [activeTab, setActiveTab] = useState<'ICE' | 'EV'>('ICE');
  const [sounds, setSounds] = useState<ApiSound[]>([]);
  const [lockedModalSound, setLockedModalSound] = useState<ApiSound | null>(null);
  const [paymentNotice, setPaymentNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    const loadData = async () => {
      try {
        const soundsRes = await api.sounds.getCatalogue().catch(() => ({ sounds: [] }));
        if (soundsRes.sounds && soundsRes.sounds.length > 0) {
          setSounds(soundsRes.sounds);
        } else {
          // Fallback static catalogue if backend is starting
          setSounds([
            { id: '1', slug: 'v8', name: 'American Crossplane V8', category: 'ICE', tagline: 'Deep rhythmic rumble with visceral low-frequency burble', isFreeGuest: true, isUnlocked: true },
            { id: '2', slug: 'v6', name: 'Twin-Turbo V6', category: 'ICE', tagline: 'Refined high-revving soundtrack with metallic intake bite', isFreeGuest: false, isUnlocked: false },
            { id: '3', slug: 'i4', name: 'High-Output Inline-4', category: 'ICE', tagline: 'Punchy rally-inspired rasp with sharp mechanical throttle bite', isFreeGuest: false, isUnlocked: false },
            { id: '4', slug: 'flat6', name: 'Naturally Aspirated Flat-6', category: 'ICE', tagline: 'Distinctive boxer howl with mechanical precision', isFreeGuest: false, isUnlocked: false },
            { id: '5', slug: 'lambo_v12', name: 'Lamborghini-inspired V12', category: 'ICE', tagline: 'Exotic 6.5L Italian symphony revving to 8,500 RPM', isFreeGuest: false, isUnlocked: false },
            { id: '6', slug: 'bmw_turbo_i4', name: 'BMW-inspired Turbo I4', category: 'ICE', tagline: 'Modern Bavarian turbo power with crisp linear delivery', isFreeGuest: false, isUnlocked: false },
            { id: '7', slug: 'bmw_i6', name: 'BMW-inspired Inline-6', category: 'ICE', tagline: 'Silky smooth 3.0L twin-scroll straight-six with mechanical harmony', isFreeGuest: true, isUnlocked: true },
            { id: '8', slug: 'bmw_v8', name: 'BMW-inspired Twin-Turbo V8', category: 'ICE', tagline: 'Authoritative hot-V 4.4L twin-turbo with ferocious torque roar', isFreeGuest: false, isUnlocked: false },
            { id: '9', slug: 'ev_cinematic', name: 'Iconic Cinematic', category: 'EV', tagline: 'Orchestral film-score soundscape with sweeping harmonic depth', isFreeGuest: true, isUnlocked: true },
            { id: '10', slug: 'ev_expressive', name: 'Iconic Expressive', category: 'EV', tagline: 'Cinematic THX swell resolving into glorious Major 9th chord', isFreeGuest: false, isUnlocked: false },
            { id: '11', slug: 'ev_relax', name: 'Iconic Relax', category: 'EV', tagline: 'Soothing massage-like sanctuary with velvety sub-bass warmth', isFreeGuest: true, isUnlocked: true },
            { id: '12', slug: 'ev_sport', name: 'Iconic Sport', category: 'EV', tagline: 'Aggressive electric hyper-drive with visceral acceleration bite', isFreeGuest: false, isUnlocked: false },
            { id: '13', slug: 'ev_pulse', name: 'Iconic Pulse', category: 'EV', tagline: 'Kinetic electronic drive with dynamic load-reactive heartbeat', isFreeGuest: false, isUnlocked: false },
            { id: '14', slug: 'ev_spatial', name: 'Iconic Spatial', category: 'EV', tagline: 'Expansive 3D sound field with celestial synthetic choir', isFreeGuest: false, isUnlocked: false },
          ]);
        }
      } catch {
        // Handled via fallback
      }
    };
    loadData();
  }, [isOpen]);

  if (!isOpen) return null;

  const filteredSounds = sounds.filter((s) => s.category === activeTab);

  const handleTileClick = (sound: ApiSound) => {
    if (sound.isUnlocked || sound.isFreeGuest) {
      onSelectSound(sound.slug as EngineProfileId);
      onClose();
    } else {
      setLockedModalSound(sound);
      setPaymentNotice(null);
    }
  };

  const handleAttemptUnlock = async (packageId: string) => {
    try {
      const res = await api.payments.createPayment(packageId);
      setPaymentNotice(res.message);
    } catch {
      setPaymentNotice('Payments are coming soon. Online payments are currently on hold.');
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/80 backdrop-blur-xl p-3 md:p-6 select-none">
      <div className="relative w-full max-w-4xl max-h-[90vh] bg-slate-900 border border-slate-800 rounded-3xl p-5 md:p-8 flex flex-col shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-slate-800 mb-4">
          <div className="flex items-center gap-2.5">
            <span className="w-2.5 h-2.5 rounded-full bg-red-600 shadow-[0_0_10px_rgba(220,38,38,0.8)]" />
            <h2 className="text-xl md:text-2xl font-black font-display text-white uppercase tracking-wider">
              SOUND CATALOGUE
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl bg-slate-800/80 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Category Tabs: ICE vs EV */}
        <div className="grid grid-cols-2 gap-2 p-1 bg-slate-950 rounded-2xl border border-slate-800 mb-4">
          <button
            type="button"
            onClick={() => setActiveTab('ICE')}
            className={`py-3 rounded-xl font-display font-black text-xs md:text-sm tracking-wider uppercase transition-all cursor-pointer ${
              activeTab === 'ICE'
                ? 'bg-gradient-to-r from-red-600 to-rose-600 text-white shadow-[0_0_16px_rgba(220,38,38,0.4)]'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            COMBUSTION (ICE)
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('EV')}
            className={`py-3 rounded-xl font-display font-black text-xs md:text-sm tracking-wider uppercase transition-all cursor-pointer ${
              activeTab === 'EV'
                ? 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white shadow-[0_0_16px_rgba(6,182,212,0.4)]'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            ELECTRIC (EV)
          </button>
        </div>

        {/* Sound Tiles Grid */}
        <div className="flex-1 overflow-y-auto pr-1 grid grid-cols-1 md:grid-cols-2 gap-3">
          {filteredSounds.map((sound) => {
            const isCurrent = currentSoundId === sound.slug;
            const isUnlocked = sound.isUnlocked || sound.isFreeGuest;

            return (
              <div
                key={sound.id}
                onClick={() => handleTileClick(sound)}
                className={`p-4 rounded-2xl border transition-all cursor-pointer flex flex-col justify-between ${
                  isCurrent
                    ? 'bg-slate-800/90 border-cyan-500/80 shadow-[0_0_16px_rgba(6,182,212,0.3)]'
                    : isUnlocked
                    ? 'bg-slate-900/90 border-slate-800 hover:border-slate-700 hover:bg-slate-800/50'
                    : 'bg-slate-950/60 border-slate-850 opacity-80 hover:opacity-100 hover:border-slate-750'
                }`}
              >
                <div>
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <h3 className="font-display font-bold text-sm md:text-base text-white tracking-wide">
                      {sound.name}
                    </h3>
                    {isUnlocked ? (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-bold uppercase bg-emerald-950/80 text-emerald-400 border border-emerald-500/30 flex items-center gap-1 shrink-0">
                        <Play className="w-2.5 h-2.5 fill-current" />
                        {sound.isFreeGuest ? 'FREE' : 'READY'}
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-bold uppercase bg-amber-950/80 text-amber-300 border border-amber-500/40 flex items-center gap-1 shrink-0">
                        <Lock className="w-2.5 h-2.5" />
                        LOCKED
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 font-sans leading-relaxed line-clamp-2">
                    {sound.tagline}
                  </p>
                </div>

                <div className="mt-3 pt-2.5 border-t border-slate-800/60 flex items-center justify-between text-[11px] font-mono text-slate-400">
                  <span>{sound.category === 'ICE' ? 'Gearbox Dynamics' : 'Continuous Field'}</span>
                  {isUnlocked ? (
                    <span className="text-cyan-400 font-bold uppercase">
                      {isCurrent ? 'ACTIVE NOW' : 'TAP TO PLAY'}
                    </span>
                  ) : (
                    <span className="text-amber-400 font-bold uppercase">UNLOCK PACK</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Locked Modal / Purchase Dialog */}
        {lockedModalSound && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4">
            <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 max-w-md w-full shadow-2xl flex flex-col">
              <div className="flex items-center justify-between pb-3 border-b border-slate-800 mb-3">
                <div className="flex items-center gap-2">
                  <Lock className="w-5 h-5 text-amber-400" />
                  <h3 className="font-display font-bold text-white text-base">
                    Unlock {lockedModalSound.name}
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setLockedModalSound(null)}
                  className="p-1 rounded-lg text-slate-400 hover:text-white cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <p className="text-xs text-slate-300 font-sans leading-relaxed mb-4">
                This sound is part of the premium sound library. Choose a package to unlock lifetime access across all your devices.
              </p>

              {/* Package Options */}
              <div className="flex flex-col gap-2.5 mb-4">
                <button
                  type="button"
                  onClick={() => handleAttemptUnlock('ice_pack')}
                  className="p-3 rounded-xl bg-slate-950 border border-slate-800 hover:border-red-500/60 text-left flex items-center justify-between transition-all cursor-pointer group"
                >
                  <div>
                    <div className="font-display font-bold text-white text-xs md:text-sm group-hover:text-red-400">
                      ICE SOUND PACK
                    </div>
                    <div className="text-[10px] text-slate-400 font-mono">
                      All 8 Combustion Engines
                    </div>
                  </div>
                  <span className="font-mono font-black text-sm text-red-400">
                    $3.99
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => handleAttemptUnlock('ev_pack')}
                  className="p-3 rounded-xl bg-slate-950 border border-slate-800 hover:border-cyan-500/60 text-left flex items-center justify-between transition-all cursor-pointer group"
                >
                  <div>
                    <div className="font-display font-bold text-white text-xs md:text-sm group-hover:text-cyan-400">
                      EV SOUND PACK
                    </div>
                    <div className="text-[10px] text-slate-400 font-mono">
                      All 6 Cinematic EV Soundscapes
                    </div>
                  </div>
                  <span className="font-mono font-black text-sm text-cyan-400">
                    $3.99
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => handleAttemptUnlock('complete_pack')}
                  className="p-3 rounded-xl bg-gradient-to-r from-red-950/60 to-cyan-950/60 border border-slate-700 hover:border-amber-500/60 text-left flex items-center justify-between transition-all cursor-pointer group"
                >
                  <div>
                    <div className="font-display font-bold text-white text-xs md:text-sm group-hover:text-amber-300 flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                      REVIVE COMPLETE
                    </div>
                    <div className="text-[10px] text-slate-400 font-mono">
                      All 14 ICE + EV Sound Profiles
                    </div>
                  </div>
                  <span className="font-mono font-black text-sm text-amber-300">
                    $6.99
                  </span>
                </button>
              </div>

              {/* Notice when clicked */}
              {paymentNotice && (
                <div className="p-3 rounded-xl bg-amber-950/70 border border-amber-500/50 text-[11px] font-mono text-amber-300 mb-3 text-center">
                  {paymentNotice}
                </div>
              )}

              <button
                type="button"
                onClick={() => setLockedModalSound(null)}
                className="py-2.5 rounded-xl bg-slate-800 hover:bg-slate-750 text-slate-300 font-mono font-bold text-xs uppercase cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
